import { Typography } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import Dialog from '../portico/dialog/dialog';
import Spacing from '../portico/spacing/spacing';
import React, { useState, useEffect } from 'react';
import { useIdleTimer, IIdleTimer } from 'react-idle-timer'
import { useDispatch } from 'react-redux';
import STATIC_TEXT from '../../content/common.json';
import { logout } from '../../stores/async/identity-async-actions';
import { useUser } from '../../stores/user/user.hooks';
import CONSTANTS from '../../utils/constants';
import { isLoginExpiringInXSeconds, refreshUserSession } from '../../utils/identity-utils';
import { useSessionTimeOut } from './session-time-out.styles';

class SessionTimeOutProps {
  userAuthenticated: boolean;
  idleTimeoutDuration: number;
  refreshBufferDuration: number;
  eventThrottleDuration: number;
  promptBeforeIdleDuration: number;
}

const SessionMgmt: React.FC<SessionTimeOutProps> = ({
  userAuthenticated,
  refreshBufferDuration,
  idleTimeoutDuration,
  eventThrottleDuration,
  promptBeforeIdleDuration
}) => {
  const classes = useSessionTimeOut();

  const promptDurationSeconds = CONSTANTS.PROMPT_BEFORE_IDLE_DURATION / 1000

  const [openDialog, setOpenDialog] = useState(false);
  const [counter, setCounter] = useState(promptDurationSeconds);
  const [intervalID, setIntervalID] = useState(null);

  const dispatch = useDispatch();

  const user = useUser();

  const showPrompt = () => {
      if (!intervalID) {
        startTimer(promptDurationSeconds - 1);
      }
      setOpenDialog(true);
  }
  const hidePrompt = () => {
      if (intervalID) {
        clearInterval(intervalID);
        setIntervalID(null)
      }
      setOpenDialog(false);
  }

  const onAction = (event?: Event, idleTimer?: IIdleTimer) => {
    // A focus event fires when the prompt is displayed
    // This has the unfortunate effect of refreshing the user even though
    // they did not interact with the application. This could probably also
    // be handled by specifying the events to listen to when calling useIdleTimer
    // but, I like the defaults and decided to be explicit with this guard clause.
    if (event?.type == 'focus') {
      return
    }
    if (!idleTimer.isPrompted() && isLoginExpiringInXSeconds(refreshBufferDuration)) {
      refreshUserSession();
    }
  }
  const onPrompt = (event?: Event, idleTimer?: IIdleTimer) => {
    showPrompt()
  }

  const onIdle = (event?: Event, idleTimer?: IIdleTimer) => {
    idleTimer.pause()
    hidePrompt()
    dispatch(logout(true))
  }

  const {start, activate} = useIdleTimer({
    startManually: true,
    timeout: idleTimeoutDuration,
    promptBeforeIdle: promptBeforeIdleDuration,
    eventsThrottle: eventThrottleDuration,
    throttle: eventThrottleDuration,
    onAction,
    onPrompt,
    onIdle
  })

  useEffect(() => {
    if (userAuthenticated) {
      start()
    }
  }, [userAuthenticated])

  const startTimer = (duration: number): void => {
    let timer = duration;
    const id = setInterval(() => {
      setCounter(timer);
      if (--timer < 0) {
        hidePrompt()
      }
    }, 1000);
    setIntervalID(id);
  };

  const onContinue = async () => {
    hidePrompt()
    activate()
    refreshUserSession()
  };

  const onLogOut = () => {
    dispatch(logout());
  };

  return (
    <>
      <Dialog
        titleIcon={<WarningIcon className={classes.titleIcon} fontSize="large"/>}
        title={`${STATIC_TEXT.sessionTimeoutModal.title1} ${counter} ${STATIC_TEXT.sessionTimeoutModal.title2}`}
        open={openDialog}
        okBtnLabel={STATIC_TEXT.sessionTimeoutModal.okBtnTitle}
        cancelBtnLabel={STATIC_TEXT.sessionTimeoutModal.cancelBtn}
        onOk={onContinue}
        onCancel={onLogOut}
        disableActions={false}
        okBtnClasses={classes.btn_primary}
      >
        <Typography variant='body1'>{STATIC_TEXT.sessionTimeoutModal.description1}</Typography>
        <Spacing variable="spacing-base-x3"/>
        <Typography variant='body1'>{STATIC_TEXT.sessionTimeoutModal.description2}</Typography>
      </Dialog>
    </>
  );
};

export default SessionMgmt;